import { useMemo } from "preact/hooks";
import { scaleLinear, scaleOrdinal, scaleBand } from "d3-scale";
import { arc, stack } from "d3-shape";
import { max, range } from "d3-array";
import { useChartDimensions } from "./useChartDimensions";

const Ring = ({ d, angleOffset, fill, makeArc }) => (
  <g class="ring" fill={fill}>
    {d.map((e) => (
      <path d={makeArc(e)} transform={`rotate(${angleOffset})`} />
    ))}
  </g>
);

const Label = ({ d, angleOffset, x, outerRadius }) => {
  const inLeftHalf = (d) =>
    (x(d.angle) + x.bandwidth() / 2 + Math.PI / 2) % (2 * Math.PI) < Math.PI;

  const transformLabelText = inLeftHalf(d)
    ? "rotate(90)translate(0,6)"
    : "rotate(-90)translate(0,6)";

  return (
    <g
      class="label"
      text-anchor="middle"
      transform={`rotate(${
        ((x(d.angle) + x.bandwidth() / 2) * 180) / Math.PI - (90 - angleOffset)
      }) translate(${outerRadius + 30},0)`}
    >
      <text transform={transformLabelText}>{d.angle}</text>
    </g>
  );
};

const Legend = ({ bins, binsTitle, binUnits, colorScale, outerRadius }) => (
  <g class="legend" transform={`translate(${outerRadius - 10},0)`}>
    <text
      text-decoration="underline"
      text-anchor="end"
      transform={`translate(40,${-outerRadius - 30})`}
    >
      {binsTitle} {binUnits ? `(${binUnits})` : null}
    </text>
    {/* As arrays are REFERENCED, using bins.reverse() flips the array on every render, therefore copy it */}
    {[...bins].reverse().map((legendEntry, i) => (
      <g transform={`translate(0,${-outerRadius - 25 + i * 20})`}>
        <rect
          fill={colorScale(legendEntry)}
          width="18"
          height="18"
          stroke="dimgray"
          stroke-width="0.5"
        />
        <text x="24" y="9" dy="0.35em" font-family="sans-serif" font-size="13">
          {legendEntry}
        </text>
      </g>
    ))}
  </g>
);

const WindRose = ({
  binsTitle = "",
  data,
  bins,
  binUnits,
  yUnits,
  colorSchemeColors,
}) => {
  const [ref, dimensions] = useChartDimensions();

  const innerRadius = 30;
  const outerRadius =
    Math.min(dimensions.boundedWidth, dimensions.boundedHeight) / 2;

  const y = scaleLinear()
    .domain([0, max(data, (d) => d.total)])
    .range([innerRadius, outerRadius]);

  const colorScale = useMemo(
    () => scaleOrdinal().domain(bins).range(colorSchemeColors),
    [bins, colorSchemeColors]
  );

  // the x scale is for the position of the bars along the circle, one full turn
  const x = useMemo(
    () =>
      scaleBand()
        .domain(data.map((d) => d.angle))
        .range([0, 2 * Math.PI])
        .align(0),
    [data]
  );

  const makeArc = useMemo(
    () =>
      arc()
        .innerRadius((d) => y(d[0]))
        .outerRadius((d) => y(d[1]))
        .startAngle((d) => x(d.data.angle))
        .endAngle((d) => x(d.data.angle) + x.bandwidth())
        .padAngle(0.02)
        .padRadius(innerRadius),
    [innerRadius, x, y]
  );

  const yTicks = y.ticks(5); // TODO: nrOfYTicks
  // const tickFormat = y.tickFormat(x => `${x}\%`);

  const stackedData = stack().keys(bins)(data);
  const angleOffset = -360.0 / data.length / 2.0; // to shift each bar to middle of line
  const yLineStep = 360 / data.length;

  return (
    <div id="chart-wrapper" ref={ref}>
      <svg
        viewBox={`0, 0, ${dimensions.width + 20}, ${dimensions.height + 20}`}
        width={dimensions.width}
        height={dimensions.height}
        id="windrose"
      >
        <text
          fill="rgb(120, 120, 120)"
          fontSize="14"
          y={dimensions.height - 15}
        >
          Made with{" "}
          <a href="https://windrose.xyz/">
            <tspan font-eight="700" fill="rgb(100, 100, 100)">
              windrose.xyz
            </tspan>
          </a>
        </text>
        <g
          transform={`translate(${dimensions.width / 2},${
            dimensions.height / 2
          })`}
        >
          <g class="axes">
            {range(0, 360, yLineStep).map((d) => (
              <line
                x1={innerRadius}
                x2={y(yTicks.reverse()[0])} // to the last tick circle
                transform={`rotate(${d - 90})`}
                fill="none"
                stroke="gray"
                stroke-dasharray="1,4"
              />
            ))}
          </g>
          <g class="rings">
            {stackedData.map((d) => (
              <Ring
                d={d}
                angleOffset={angleOffset}
                fill={colorScale(d.key)}
                makeArc={makeArc}
              />
            ))}
          </g>
          <g
            class="direction-labels"
            font-weight="600"
            font-size="16"
            font-family="sans-serif"
          >
            {data.map((d, i) => (
              <Label
                key={i}
                d={d}
                angleOffset={angleOffset}
                x={x}
                outerRadius={outerRadius}
              />
            ))}
          </g>
          <g
            class="y-ticks"
            text-anchor="middle"
            font-family="sans-serif"
            font-size="18"
          >
            {yTicks.map((tick) => (
              <g class="y-circle-tick">
                <circle
                  fill="none"
                  stroke="gray"
                  stroke-dasharray="4,4"
                  r={y(tick)}
                />
                <text
                  y={-y(tick)}
                  dy="-0.35em"
                  x="-8"
                  paint-order="stroke"
                  stroke-width="1.4px"
                  stroke="white"
                >
                  {tick}
                </text>
              </g>
            ))}
            <text
              y={-outerRadius + 22}
              x="30"
              font-size="18"
              paint-order="stroke"
              stroke-width="1.4px"
              stroke="white"
              id="yUnitsText"
            >
              {yUnits}
            </text>
          </g>
          <Legend
            bins={bins}
            binsTitle={binsTitle}
            colorScale={colorScale}
            outerRadius={outerRadius}
            binUnits={binUnits}
          />
        </g>
      </svg>
    </div>
  );
};

export { WindRose };
